import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";

type ProfileFormData = {
  email: string;
  company_name: string;
};

export const ProfileForm = () => {
  const { toast } = useToast();
  
  const form = useForm<ProfileFormData>({
    defaultValues: {
      email: "",
      company_name: "",
    },
  });

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error("Error fetching user:", error);
          return;
        }

        if (user) {
          console.log("Setting email for user:", user.email);
          form.setValue("email", user.email || "");
          
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("company_name")
            .maybeSingle();

          if (profileError) {
            console.error("Error fetching profile:", profileError);
            return;
          }

          if (profile) {
            form.setValue("company_name", profile.company_name || "");
          }
        }
      } catch (error) {
        console.error("Unexpected error:", error);
      }
    };

    getCurrentUser();
  }, [form]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        email: data.email || undefined,
      });

      if (updateError) throw updateError;

      if (data.company_name) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({ company_name: data.company_name })
          .single();

        if (profileError) throw profileError;
      }

      toast({
        title: "Success",
        description: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Profile update error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update profile",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter new email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="company_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter company name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit">
          Update Profile
        </Button>
      </form>
    </Form>
  );
};